<script>
  import { MountingPortal } from 'portal-vue';
  import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

  import ENV from '@/scripts/vue/helpers/ENV';
  import '@/scripts/helpers/vhNormalizer';
  import CarouselLazyImage from '@/scripts/vue/components/CarouselLazyImage.vue';
  import FocusLock from '@/scripts/vue/components/FocusLock.vue';
  import ZIndexStack from '@/scripts/vue/components/ZIndexStack.vue';
  import { Carousel } from '@/scripts/vue/components/Carousel';
  import IconClose from '@/assets/icons/close.svg?component';
  import Panzoom from './Panzoom.vue';

  export default {
    components: {
      CarouselLazyImage,
      Carousel,
      MountingPortal,
      FocusLock,
      ZIndexStack,
      Panzoom,
      IconClose,
    },
    props: {
      items: {
        type: Array,
        default: () => ([]),
      },
      active: {
        type: Boolean,
        default: false,
      },
      mountTo: {
        type: String,
        default: '#modals',
      },
      startIndex: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        carouselIndex: 0,
        isCarouselSliding: false,
        inFocus: false,
        mutableActive: false,
        isCarouselPaused: false,
        resetCarouselOnSlideEnd: false,
        carouselThreshold: 20,
      };
    },
    computed: {
      panzoomActive() {
        return ENV.userTouching;
      },
    },
    watch: {
      active(val) {
        if (val === this.mutableActive) return;

        if (val) {
          this.activate();
        } else {
          this.deactivate();
        }
      },
    },
    mounted() {
      if (this.active && !this.mutableActive) {
        this.activate();
      }
    },
    beforeDestroy() {
      if (this.mutableActive) {
        this.deactivate();
      }
    },
    methods: {
      activate() {
        this.$emit('activate');
        this.sliderIndex = this.startIndex;
        this.mutableActive = true;
        this.setEvents();
        this.$nextTick(() => {
          this.$nextTick(() => {
            disableBodyScroll(this.$refs.root.$el, { reserveScrollBarGap: true });
            this.$emit('activated');
          });
        });
      },
      deactivate() {
        this.$emit('deactivate');
        this.mutableActive = false;
        this.setEvents(false);
        enableBodyScroll(this.$refs.root.$el);
        this.$nextTick(() => {
          this.$emit('deactivated');
        });
      },
      setEvents(add = true) {
        const action = add ? 'addEventListener' : 'removeEventListener';
        document[action]('keyup', this.onEsc);
      },
      onEsc(e) {
        if (this.inFocus && e.key === 'Escape' && this.mutableActive) {
          this.deactivate();
        }
      },
      onFocusLock(locked) {
        this.inFocus = locked;
      },
      carouselNext() {
        if (!this.$refs.carousel) return;
        this.$refs.carousel.next();
      },
      carouselPrev() {
        if (!this.$refs.carousel) return;
        this.$refs.carousel.prev();
      },
      carouselSlideTo(index) {
        if (!this.$refs.carousel) return;
        this.$refs.carousel.slideTo(index);
      },
      onCarouselSlideStart() {
        this.isCarouselSliding = true;
      },
      onCarouselSlideEnd() {
        this.isCarouselSliding = false;
        if (this.resetCarouselOnSlideEnd) {
          this.$nextTick(() => {
            this.resetCarouselOnSlideEnd = false;
            this.carouselThreshold = 20;
          });
        }
      },
      onClickCarouselItem() {
        if (this.isCarouselSliding) return;
        this.isShowFullscreenGallery = true;
      },
      onPanzoomChange(e, itemIndex) {
        if (itemIndex !== this.carouselIndex) return;

        // If scale > 1 zoom mode active, carousel paused
        // In case we move zoomed element more than 50% of screen - Panzoom resets
        // And we slide carousel manually at the same time
        // To prevent extra slide we set big carousel threshold until manual slide done

        const { scale, x } = e.detail;

        const width = this.$refs.carousel.$el.offsetWidth;
        let autoSlide = false;

        if (x >= width * 0.4) {
          this.carouselPrev();
          autoSlide = true;
        } else if (x <= width * -0.4) {
          this.carouselNext();
          autoSlide = true;
        }

        if (autoSlide) {
          this.resetCarouselOnSlideEnd = true;
          this.carouselThreshold = 2000;
        }

        this.isCarouselPaused = scale > 1;
      },
      handleTouchStartEvent(e) {
        if (this.panzoomActive) return;
        e.stopPropagation();
      },
      handleTouchMoveEvent(e) {
        if (this.panzoomActive) return;
        e.stopPropagation();
        e.preventDefault();
      },
      handleMouseDownEvent(e) {
        if (this.panzoomActive) return;
        e.stopPropagation();
        e.preventDefault();
      },
      handleMouseMoveEvent(e) {
        if (this.panzoomActive) return;
        e.stopPropagation();
        e.preventDefault();
      },
    },
  };
</script>

<template>
  <MountingPortal
    :mount-to="mountTo"
    append
  >
    <div
      v-if="mutableActive"
      class="product-fullscreen-gallery"
    >
      <ZIndexStack
        ref="root"
        class="product-fullscreen-gallery__content"
      >
        <FocusLock
          @activate="onFocusLock(true)"
          @deactivate="onFocusLock(false)"
        >
          <div
            class="product-fullscreen-gallery__inner"
          >
            <button
              type="button"
              class="product-fullscreen-gallery__close"
              @click="deactivate"
            >
              <IconClose class="product-fullscreen-gallery__close-icon" />
            </button>
            <Carousel
              ref="carousel"
              v-model="carouselIndex"
              class="product-fullscreen-gallery__carousel"
              :loop="items.length > 1"
              :paused="isCarouselPaused"
              :handle-touch-start-event="handleTouchStartEvent"
              :handle-touch-move-event="handleTouchMoveEvent"
              :handle-mouse-down="handleMouseDownEvent"
              :handle-mouse-move="handleTouchMoveEvent"
              :threshold="carouselThreshold"
              @slide-start="onCarouselSlideStart"
              @slide-end="onCarouselSlideEnd"
            >
              <div
                v-for="(item, i) in items"
                :key="i"
                class="product-fullscreen-gallery__carousel-item"
              >
                <Panzoom
                  :active="panzoomActive && carouselIndex === i"
                  @panzoomchange="onPanzoomChange($event, i)"
                >
                  <CarouselLazyImage
                    :active="carouselIndex === i"
                    :src="item"
                    alt=""
                  />
                </Panzoom>
              </div>
            </Carousel>
            <div
              v-if="items.length > 1"
              class="product-fullscreen-gallery__dots"
            >
              <button
                v-for="(dot, i) in items"
                :key="i"
                type="button"
                class="product-fullscreen-gallery__dot"
                :class="{ '_active': i === carouselIndex }"
                @click="carouselSlideTo(i)"
              />
            </div>
          </div>
        </FocusLock>
      </ZIndexStack>
    </div>
  </MountingPortal>
</template>

<style lang="scss" type="text/scss" scoped>
  .product-fullscreen-gallery {
    &__content {
      position: fixed;
      min-width: 320px;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      background: #fff;
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
    }

    &__inner {
      // Normalized 100vh for iOS
      // Required --vh css variable
      height: 100vh;
      height: calc(var(--vh, 1vh) * 100);
      display: flex;
      flex-direction: column;
    }

    &__close {
      @include reset-button;
      position: absolute;
      z-index: 90;
      right: 10px;
      top: 10px;
      padding: 3px;

      &-icon {
        width: 24px;
        height: 24px;
        fill: $color-fill;
      }
    }

    &__carousel {
      position: relative;
      z-index: 80;
      height: 100%;

      & /deep/ > * {
        height: 100%;
        outline: none;
      }

      &-item {
        position: relative;
        display: block;
        height: 100%;
        overflow: hidden;

        /deep/ > div {
          position: relative;
          width: 100%;
          height: 100%;
        }

        /deep/ img {
          @include centerer;
          width: 100%;
          height: 100%;
          object-fit: contain;
          pointer-events: none;

          @include ie10-11 {
            height: 100%;
            width: auto;
          }
        }
      }
    }

    &__dots {
      padding: 15px 0;
      text-align: center;
    }

    &__dot {
      @include reset-button;
      margin: 0 5px;
      padding: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: $color-fill;

      &._active {
        background: $color-brand;
      }
    }
  }

</style>
